import { parseResetInstant } from "./reset-parse.js";

export interface UsageGaugeReading {
  /** Panel label as rendered, e.g. "Current session", "Current week (Fable)". */
  label: string;
  /** 0–1 utilization fraction. */
  utilization: number;
  /** Absolute ISO instant this gauge resets at, or null if unparseable/absent. */
  resetsAt?: string | null;
}

export interface UsageGauges {
  /** 0–1 utilization for the current 5-hour session window, or null if absent. */
  sessionUsage: number | null;
  /** 0–1 utilization; max across all weekly gauges (all-models + per-model), or null. */
  weeklyUsage: number | null;
  /** Absolute ISO instant the session gauge resets at, or null. */
  sessionResetsAt: string | null;
  /** Soonest absolute ISO instant among weekly gauges, or null. */
  weeklyResetsAt: string | null;
  /** Every gauge the panel rendered, labels preserved (session + each weekly row). */
  gauges: UsageGaugeReading[];
}

// ANSI CSI (colors / cursor moves) + OSC sequences left in the raw TUI capture.
// Built via RegExp from the ESC codepoint so no literal control char is embedded
// in the source (which would trip no-control-regex / confuse editors).
const ESC = "\\u001b";
const ANSI_CSI = new RegExp(`${ESC}\\[[0-9;?]*[ -/]*[@-~]`, "g");
const ANSI_OSC = new RegExp(`${ESC}\\][^\\u0007${ESC}]*(?:\\u0007|${ESC}\\\\)`, "g");
// Box-drawing + block/bar glyphs (U+2500–U+259F) the panel draws its gauges with.
const BAR_GLYPHS = /[─-▟]/g;

/**
 * Normalize `/usage` output into plain, gauge-parseable text.
 *
 * The interactive `/usage` panel — the only place the CLI renders the session /
 * weekly gauges (`claude -p /usage` omits them entirely as of CLI 2.x) — is
 * drawn with ANSI escapes and Unicode progress-bar glyphs that glue directly
 * onto the percentage (e.g. `Current session███…30%used`). Stripping them
 * leaves the label and number separated by plain whitespace, which also matches
 * the older `claude -p "/usage"` plain-text layout.
 */
function normalizeUsageText(stdout: string): string {
  return stdout
    .replace(ANSI_CSI, "")
    .replace(ANSI_OSC, "")
    .replace(BAR_GLYPHS, " ")
    .replace(/\r/g, "");
}

/**
 * Parse the text of `/usage` into the two gauge numbers we store. Best-effort:
 * any gauge that isn't present becomes null so the poller can skip reporting
 * (e.g. API-key pods have no subscription gauges).
 *
 * Handles both layouts:
 *   - legacy `claude -p "/usage"`: `Current session: 30% used`
 *   - current interactive panel:   `Current session … 30%used` (no colon,
 *     percentage optionally glued to `used`).
 */
export function parseUsageGauges(
  stdout: string,
  now = Date.now(),
  defaultTz?: string,
): UsageGauges {
  const text = normalizeUsageText(stdout);
  // One pattern for every gauge row: the kind (session/week), an optional
  // parenthetical model qualifier ("(all models)", "(Fable)"), then the
  // percentage, then the trailing reset phrase (if any). The qualifier must
  // be captured BEFORE the lazy filler so per-model weekly rows keep their
  // identity instead of collapsing. The trailing reset-phrase capture is
  // bounded by a lookahead for the next "Current session|week" row, a
  // newline, or end of string — the interactive panel glues consecutive
  // rows onto the same line with no newline separator, so an unbounded
  // `[^\n]*` would swallow every row after the first.
  const rows = [
    ...text.matchAll(
      /Current (session|week)\s*(\([^)\n]*\))?[^%\n]*?(\d+(?:\.\d+)?)\s*%(?:\s*used)?([^\n]*?)(?=Current\s+(?:session|week)\b|\n|$)/gi,
    ),
  ];

  const gauges: UsageGaugeReading[] = rows.map((m) => ({
    label: `Current ${m[1].toLowerCase()}${m[2] ? ` ${m[2]}` : ""}`,
    utilization: Number(m[3]) / 100,
    resetsAt: parseResetInstant(m[4] ?? "", now, defaultTz),
  }));

  const session = gauges.find((g) => g.label.startsWith("Current session"));
  const weekly = gauges.filter((g) => g.label.startsWith("Current week"));
  const weeklyResets = weekly.map((g) => g.resetsAt).filter((r): r is string => r !== null);
  return {
    sessionUsage: session ? session.utilization : null,
    weeklyUsage: weekly.length ? Math.max(...weekly.map((g) => g.utilization)) : null,
    sessionResetsAt: session?.resetsAt ?? null,
    // Soonest weekly reset (all weekly rows share one instant in practice).
    weeklyResetsAt: weeklyResets.length
      ? new Date(Math.min(...weeklyResets.map((r) => new Date(r).getTime()))).toISOString()
      : null,
    gauges,
  };
}
